import React, {useEffect, useRef, useState} from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {
    getTransferRequest,
    rejectTransferRequest,
    selectTransferRequestSerials,
    TransferRequest
} from '../transferUtils'
import {webEvents} from '../../common/WebEvents'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'

const TransferSerialSelection = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const {id} = useParams()
    const [currentSerial, setCurrentSerial] = useState<string>('')
    const [selectedSerials, setSelectedSerials] = useState<string[]>([])
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [action, setAction] = useState<string>('')
    const [quantityExceeded, setQuantityExceeded] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState(false)
    const [transferSerialRequest, setTransferSerialRequest] = useState(location.state?.transferSerialRequest)
    let inputRef = useRef<HTMLInputElement>(null)
    const dispatch = useDispatch()

    useEffect(() => {
        if (id) {
            setIsLoading(true)
            getTransferRequest(Number(id))
                .then((t: TransferRequest) => {
                    setTransferSerialRequest({
                        tk: t.toLocationId,
                        quantity: t.quantity,
                        sku: t.sku
                    })
                })
                .catch(error => {
                    webEvents.error(
                        error.message,
                        {'transfer_review': id},
                        error.stack
                    )
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                    navigate('/inventory/transfer-requests')
                }).finally(() => setIsLoading(false))
        } else {
            navigate('/inventory/transfer-requests')
        }
    }, [])

    const handleAddSerial = () => {
        if (selectedSerials.length === transferSerialRequest.quantity) {
            setQuantityExceeded(true)
        } else if (currentSerial && currentSerial.length > 0 && currentSerial.length < 128) {
            setSelectedSerials([...selectedSerials, currentSerial])
        }
        setCurrentSerial('')
    }

    const handleReject = () => {
        const rejectReason = 'out-of-stock'
        if (id) {
            rejectTransferRequest(parseInt(id), rejectReason).then(() => {
                webEvents.info('Reject transfer Request', {
                    transferRequestId: id
                })
                window.sessionStorage.setItem('receiveSuccess', 'false')
                navigate('/inventory/transfers', {state: {submissionSuccess: false, reject: true}})
            }).catch(error => {
                webEvents.error(
                    `Error rejecting request: ${error.message}`,
                    {transferRequestId: id},
                    error
                )
                const causeMessage = error.cause
                    ? error.cause.messages[0]
                    : error.messages[0]
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
        }
    }

    const handleOpenDialog = (type: string) => {
        if (type === 'reject') {
            setAction('reject')
        } else {
            setAction('approve')
        }
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
        setAction('')
    }

    const submitSerials = () => {
        if (id) {
            selectTransferRequestSerials(parseInt(id), selectedSerials).then(() => {
                handleClose()
                navigate('/inventory/transfers', {state: {submissionSuccess: true}})
            }).catch(error => {
                webEvents.error(
                    error.message,
                    {'submitting serials for id': id},
                    error.stack
                )
                const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
        }

    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            handleAddSerial()
        }
    }

    const handleRemoveSerial = (index: number) => {
        const updatedSerials = selectedSerials.filter((_, serialIndex) => serialIndex !== index)
        setSelectedSerials(updatedSerials)
        setQuantityExceeded(false)
    }

    return (
        <>
            {isLoading ? <CircularProgress /> : (
                <>
                    <Dialog
                        open={openDialog}
                        onClose={handleClose}
                        sx={{minWidth: '450px'}}
                    >
                        <DialogTitle>
                            {action === 'reject' ? 'Item not available' : 'Confirm Approval'}
                        </DialogTitle>
                        <DialogContent>
                            <Typography>
                                {action === 'reject' ? 'You are about to confirm you do not have the item requested.' +
                                    ' Please confirm if you wish to continue.' : 'You are about to confirm you have the item requested.' +
                                    ' Please confirm if you wish to continue.'}
                            </Typography>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                            <Button onClick={handleClose} color="error">
                                Cancel
                            </Button>
                            <Button
                                onClick={action === 'reject' ? handleReject : submitSerials}
                                color="primary">
                                Confirm
                            </Button>
                        </DialogActions>
                    </Dialog>


                    <Box
                        flex={1}
                        p={2}
                        position="relative"
                        className="w-full max-w-6xl mx-auto" justifyContent="flex-end" gap={2}
                    >
                        <Typography variant="h4" component="h1" className="font-bold mb-4 mt-4 text-center">
                            Select Serials
                        </Typography>
                        <Box
                            className="flex justify-center bg-white p-4 rounded-lg shadow-lg max-w-2xl mb-6 mt-6 mx-auto">
                            <Typography className="font-bold text-gray-800 mb-1 mx-4 text-center pr-3">
                                Truck Requesting: <span
                                className="font-semibold text-gray-600">{transferSerialRequest?.tk}</span>
                            </Typography>
                            <Typography className="font-bold text-gray-800 mb-1 mx-4 text-center pr-3">
                                Item Requested: <span
                                className="font-semibold">{transferSerialRequest?.sku}</span>
                            </Typography>
                            <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center">
                                Quantity Requested: <span
                                className="font-semibold text-gray-600">{transferSerialRequest?.quantity}</span>
                            </Typography>
                        </Box>
                        <Typography variant="h5">Scan/Enter Serials</Typography>

                        <TextField
                            label={`Scan or Type Serial`}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={currentSerial}
                            onChange={e => {
                                setCurrentSerial(e.target.value.trim())
                                setQuantityExceeded(false)
                            }
                            }
                            onKeyDown={handleKeyDown}
                            placeholder="Scan or enter a serial number"
                            disabled={window.sessionStorage.getItem('blocking') === 'true'}
                            inputRef={inputRef}

                        />
                        {quantityExceeded && <Typography
                            variant="caption"
                            color="error"
                            sx={{
                                mt: 1,
                                display: 'block',
                                fontWeight: 500
                            }}
                        >
                            Quantity Entered exceeds the quantity requested
                        </Typography>}

                        <TableContainer component={Paper} className="w-full max-w-6xl mx-auto">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{display: 'none'}}></TableCell>
                                        <TableCell align="center">Item</TableCell>
                                        <TableCell align="center">Remove</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedSerials.length > 0 ? (selectedSerials.map(((item, index) => {
                                            return (
                                                <TableRow
                                                    key={index}
                                                >
                                                    <TableCell
                                                        sx={{display: 'none'}}></TableCell>
                                                    <TableCell align="center">
                                                        {item}
                                                    </TableCell>

                                                    <TableCell align="center">
                                                        <IconButton
                                                            sx={{cursor: 'pointer'}}
                                                            size="small"
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handleRemoveSerial(index)
                                                            }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                No serial items
                                            </TableCell>
                                        </TableRow>
                                    )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                    <Box mt={4} display="flex" className="w-full max-w-6xl mx-auto mb-4" justifyContent="flex-end"
                         gap={2}>
                        <Button
                            onClick={() => handleOpenDialog('reject')}
                            variant="contained"
                            color="secondary"
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
                        >
                            Don't have this Item
                        </Button>

                        <Button
                            onClick={() => handleOpenDialog('approve')}
                            variant="contained"
                            color="primary"
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
                            disabled={selectedSerials.length !== transferSerialRequest?.quantity}
                        >
                            Submit Serials
                        </Button>
                    </Box>
                </>
            )}
        </>

    )

}

export default TransferSerialSelection
